import api from "../api/api";
const BASE_URL = import.meta.env.VITE_APP_API_URL;

export const transferenciaEstoqueService = {
  /**
   * Listar todas as transferências com filtros opcionais
   * @param {Object} filtros - Filtros para a busca
   * @param {string} filtros.status - Status da transferência (PENDENTE, APROVADA, REJEITADA, CONCLUIDA, CANCELADA)
   * @param {number} filtros.id_deposito_origem - ID do depósito de origem
   * @param {number} filtros.id_deposito_destino - ID do depósito de destino
   * @param {string} filtros.data_inicio - Data de início (YYYY-MM-DD)
   * @param {string} filtros.data_fim - Data de fim (YYYY-MM-DD)
   * @param {number} filtros.id_usuario_solicitante - ID do usuário solicitante
   * @returns {Promise<Object>} Resposta com lista de transferências
   */
  async listarTransferencias(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.id_deposito_origem) params.append('id_deposito_origem', filtros.id_deposito_origem);
      if (filtros.id_deposito_destino) params.append('id_deposito_destino', filtros.id_deposito_destino);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
      if (filtros.id_usuario_solicitante) params.append('id_usuario_solicitante', filtros.id_usuario_solicitante);
      if (filtros.arquivo_gerado) params.append('arquivo_gerado', filtros.arquivo_gerado);

      const url = `${BASE_URL}/transferencias-estoque?${params.toString()}`;
      console.log("🔍 Listando transferências:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        // A API retorna dados paginados do Laravel em response.data.data
        const paginationData = response.data.data || response.data;
        const transferencias = paginationData.data || paginationData.dados || [];
        const total = paginationData.total || 0;

        console.log("✅ Transferências recebidas:", transferencias.length);
        
        return {
          success: true,
          message: response.data.mensagem || "Transferências listadas com sucesso",
          total: total,
          dados: transferencias,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao listar transferências.");
      }
    } catch (error) {
      console.error("❌ Erro ao listar transferências:", error);
      handleError(error, "Erro ao listar transferências.");
      return {
        success: false,
        message: "Erro ao listar transferências.",
        total: 0,
        dados: [],
      };
    }
  },

  /**
   * Buscar uma transferência específica por ID
   * @param {number} id - ID da transferência
   * @returns {Promise<Object>} Resposta com dados da transferência
   */
  async buscarTransferencia(id) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}`;
      console.log("🔍 Buscando transferência:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência encontrada",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar transferência:", error);
      handleError(error, "Erro ao buscar transferência.");
      return {
        success: false,
        message: "Erro ao buscar transferência.",
        dados: null,
      };
    }
  },

  /**
   * Criar uma nova transferência de estoque
   * @param {Object} dados - Dados da transferência
   * @param {number} dados.id_deposito_origem - ID do depósito de origem
   * @param {number} dados.id_deposito_destino - ID do depósito de destino
   * @param {string} dados.observacao - Observação da transferência
   * @param {Array} dados.itens - Array de itens da transferência
   * @returns {Promise<Object>} Resposta com dados da transferência criada
   */
  async criarTransferencia(dados) {
    try {
      const url = `${BASE_URL}/transferencias-estoque`;
      console.log("➕ Criando transferência:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 201 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência criada com sucesso",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao criar transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao criar transferência:", error);
      handleError(error, "Erro ao criar transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao criar transferência.",
        erros: error.response?.data?.erros || null,
      };
    }
  },

  /**
   * Atualizar uma transferência existente (apenas se estiver PENDENTE)
   * @param {number} id - ID da transferência
   * @param {Object} dados - Dados para atualizar
   * @returns {Promise<Object>} Resposta com dados da transferência atualizada
   */
  async atualizarTransferencia(id, dados) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}`;
      console.log("✏️ Atualizando transferência:", url, dados);
      
      const response = await api.put(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência atualizada com sucesso",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao atualizar transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao atualizar transferência:", error);
      handleError(error, "Erro ao atualizar transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao atualizar transferência.",
        erros: error.response?.data?.erros || null,
      };
    }
  },

  /**
   * Excluir uma transferência (apenas se estiver PENDENTE)
   * @param {number} id - ID da transferência
   * @returns {Promise<Object>} Resposta da exclusão
   */
  async excluirTransferencia(id) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}`;
      console.log("🗑️ Excluindo transferência:", url);
      
      const response = await api.delete(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência excluída com sucesso",
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao excluir transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao excluir transferência:", error);
      handleError(error, "Erro ao excluir transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao excluir transferência.",
      };
    }
  },

  /**
   * Aprovar uma transferência
   * @param {number} id - ID da transferência
   * @param {Object} dados - Dados da aprovação
   * @param {string} dados.observacao_aprovacao - Observação da aprovação (opcional)
   * @returns {Promise<Object>} Resposta da aprovação
   */
  async aprovarTransferencia(id, dados = {}) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}/aprovar`;
      console.log("✅ Aprovando transferência:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência aprovada com sucesso",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao aprovar transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao aprovar transferência:", error);
      handleError(error, "Erro ao aprovar transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao aprovar transferência.",
      };
    }
  },

  /**
   * Rejeitar uma transferência
   * @param {number} id - ID da transferência
   * @param {Object} dados - Dados da rejeição
   * @param {string} dados.motivo_rejeicao - Motivo da rejeição (obrigatório)
   * @returns {Promise<Object>} Resposta da rejeição
   */
  async rejeitarTransferencia(id, dados) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}/rejeitar`;
      console.log("❌ Rejeitando transferência:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência rejeitada com sucesso",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao rejeitar transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao rejeitar transferência:", error);
      handleError(error, "Erro ao rejeitar transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao rejeitar transferência.",
        erros: error.response?.data?.erros || null,
      };
    }
  },

  /**
   * Concluir uma transferência (executar a movimentação de estoque)
   * @param {number} id - ID da transferência
   * @param {Object} dados - Dados da conclusão
   * @param {string} dados.observacao_conclusao - Observação da conclusão (opcional)
   * @returns {Promise<Object>} Resposta da conclusão
   */
  async concluirTransferencia(id, dados = {}) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}/concluir`;
      console.log("✔️ Concluindo transferência:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência concluída com sucesso",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao concluir transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao concluir transferência:", error);
      handleError(error, "Erro ao concluir transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao concluir transferência.",
      };
    }
  },

  /**
   * Cancelar uma transferência
   * @param {number} id - ID da transferência
   * @param {Object} dados - Dados do cancelamento
   * @param {string} dados.motivo_cancelamento - Motivo do cancelamento (obrigatório)
   * @returns {Promise<Object>} Resposta do cancelamento
   */
  async cancelarTransferencia(id, dados) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}/cancelar`;
      console.log("🚫 Cancelando transferência:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Transferência cancelada com sucesso",
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao cancelar transferência.");
      }
    } catch (error) {
      console.error("❌ Erro ao cancelar transferência:", error);
      handleError(error, "Erro ao cancelar transferência.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao cancelar transferência.",
        erros: error.response?.data?.erros || null,
      };
    }
  },

  /**
   * Buscar histórico de uma transferência
   * @param {number} id - ID da transferência
   * @returns {Promise<Object>} Resposta com histórico da transferência
   */
  async buscarHistorico(id) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/${id}/historico`;
      console.log("📜 Buscando histórico:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Histórico encontrado",
          total: response.data.total,
          dados: response.data.dados,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar histórico.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar histórico:", error);
      handleError(error, "Erro ao buscar histórico.");
      return {
        success: false,
        message: "Erro ao buscar histórico.",
        total: 0,
        dados: [],
      };
    }
  },

  /**
   * Gerar arquivo Excel de transferências (SEM download automático)
   * O arquivo fica disponível no Painel SAP Downloads
   * @param {Object} dados - Dados para geração do Excel
   * @param {Array} dados.transferencias - Array de IDs das transferências
   * @param {string} dados.nome_arquivo - Nome do arquivo (opcional)
   * @returns {Promise<Object>} Resposta com dados do arquivo gerado
   */
  async gerarExcel(dados) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/gerar-excel`;
      console.log("📊 Gerando Excel:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 201 && response.data) {
        console.log("✅ Excel gerado:", response.data);
        return {
          success: true,
          message: response.data.mensagem || "Excel gerado com sucesso! Acesse o Painel SAP Downloads.",
          data: response.data.data,
        };
      } else if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Excel gerado com sucesso!",
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao gerar Excel.");
      }
    } catch (error) {
      console.error("❌ Erro ao gerar Excel:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao gerar Excel.",
      };
    }
  },

  /**
   * Baixar arquivo Excel existente
   * @param {string} nomeArquivo - Nome do arquivo Excel
   * @returns {Promise<Blob>} Arquivo Excel para download
   */
  async baixarExcel(nomeArquivo) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/baixar-excel/${nomeArquivo}`;
      console.log("📥 Baixando Excel:", url);
      
      const response = await api.get(url, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (response.status === 200) {
        // Criar URL para download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        
        // Criar link temporário e fazer download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpar URL temporária
        window.URL.revokeObjectURL(downloadUrl);

        return {
          success: true,
          message: "Excel baixado com sucesso",
          nomeArquivo: nomeArquivo,
        };
      } else {
        throw new Error("Erro ao baixar Excel.");
      }
    } catch (error) {
      console.error("❌ Erro ao baixar Excel:", error);
      handleError(error, "Erro ao baixar Excel.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao baixar Excel.",
      };
    }
  },

  /**
   * Listar arquivos Excel gerados
   * @returns {Promise<Object>} Resposta com lista de arquivos
   */
  async listarArquivosExcel() {
    try {
      const url = `${BASE_URL}/transferencias-estoque/arquivos-excel`;
      console.log("📋 Listando arquivos Excel:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivos listados com sucesso",
          dados: response.data.data || [],
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao listar arquivos.");
      }
    } catch (error) {
      console.error("❌ Erro ao listar arquivos Excel:", error);
      handleError(error, "Erro ao listar arquivos Excel.");
      return {
        success: false,
        message: "Erro ao listar arquivos Excel.",
        dados: [],
      };
    }
  },

  /**
   * Cancelar arquivo Excel gerado
   * @param {string} arquivoExportado - Nome do arquivo a cancelar
   * @returns {Promise<Object>} Resposta da operação
   */
  async cancelarArquivoExcel(arquivoExportado) {
    try {
      const url = `${BASE_URL}/transferencias-estoque/cancelar-arquivo-excel`;
      console.log("🗑️ Cancelando arquivo Excel:", url, arquivoExportado);
      
      const response = await api.post(url, { arquivo_exportado: arquivoExportado });

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivo cancelado com sucesso",
          dados: response.data.data || {},
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao cancelar arquivo.");
      }
    } catch (error) {
      console.error("❌ Erro ao cancelar arquivo Excel:", error);
      handleError(error, "Erro ao cancelar arquivo Excel.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao cancelar arquivo Excel.",
      };
    }
  },

  /**
   * Buscar depósitos disponíveis
   * @returns {Promise<Object>} Resposta com lista de depósitos
   */
  async buscarDepositos() {
    try {
      const url = `${BASE_URL}/depositos`;
      console.log("🏢 Buscando depósitos:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: "Depósitos encontrados",
          dados: response.data.dados || response.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar depósitos.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar depósitos:", error);
      handleError(error, "Erro ao buscar depósitos.");
      return {
        success: false,
        message: "Erro ao buscar depósitos.",
        dados: [],
      };
    }
  },

  /**
   * Buscar produtos disponíveis
   * @param {Object} filtros - Filtros para busca de produtos
   * @returns {Promise<Object>} Resposta com lista de produtos
   */
  async buscarProdutos(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.id_deposito) params.append('id_deposito', filtros.id_deposito);

      const url = `${BASE_URL}/produtos?${params.toString()}`;
      console.log("📦 Buscando produtos:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: "Produtos encontrados",
          dados: response.data.dados || response.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar produtos.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar produtos:", error);
      handleError(error, "Erro ao buscar produtos.");
      return {
        success: false,
        message: "Erro ao buscar produtos.",
        dados: [],
      };
    }
  },

  /**
   * Método auxiliar para formatar data para o backend
   * @param {Date|string} data - Data a ser formatada
   * @returns {string} Data formatada (YYYY-MM-DD)
   */
  formatarDataParaBackend(data) {
    if (!data) return null;
    
    const dataObj = typeof data === 'string' ? new Date(data) : data;
    
    if (isNaN(dataObj.getTime())) {
      throw new Error('Data inválida fornecida');
    }
    
    const ano = dataObj.getFullYear();
    const mes = String(dataObj.getMonth() + 1).padStart(2, '0');
    const dia = String(dataObj.getDate()).padStart(2, '0');
    
    return `${ano}-${mes}-${dia}`;
  },
};

/**
 * Função auxiliar para tratar erros
 * @param {Error} error - Objeto de erro
 * @param {string} mensagemPadrao - Mensagem padrão caso não haja mensagem específica
 */
function handleError(error, mensagemPadrao) {
  if (error.response) {
    if (error.response.status === 404) {
      throw new Error("Recurso não encontrado.");
    } else if (error.response.status === 401) {
      throw new Error("Não autorizado. Faça login novamente.");
    } else if (error.response.status === 403) {
      throw new Error("Acesso negado. Você não tem permissão para esta ação.");
    } else {
      throw new Error(error.response.data.mensagem || error.response.data.message || mensagemPadrao);
    }
  } else if (error.request) {
    throw new Error("Erro de rede. Por favor, verifique sua conexão.");
  } else {
    throw new Error(error.message || mensagemPadrao);
  }
}

export default transferenciaEstoqueService;
